import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectToJira, saveJiraDetails } from '../../modules/users';
import JiraProjectSelection from './JiraProjectSelection';
import { ArrowBack } from '@mui/icons-material';
import { jiraChannel } from '../../lib/broadcastChannels';

const JiraCallbackComponent = () => {
  const navigate = useNavigate();
  const [jiraSites, setJiraSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const query = new URLSearchParams(window.location.search);
  const stateParam = query.get('state');
  const codeParam = query.get('code');

  async function callJira() {
    try {
      if (stateParam && codeParam) {
        const response = await connectToJira(stateParam, codeParam);
        setJiraSites(response.jira_sites);
        if (response.jira_sites.length > 0) {
          setSelectedSite(response.jira_sites[0].id);
        }
        setLoading(false);
      }
    } catch (error) {
      setError('Failed to connect to Jira. Please try again.');
      setLoading(false);
    }
  }

  const handleSubmit = async () => {
    if (selectedSite) {
      const selectedSiteData = jiraSites.find(
        (site) => site.id === selectedSite
      );
      const siteUrl = selectedSiteData.url;
      try {
        await saveJiraDetails({
          cloudId: selectedSite,
          siteUrl,
          projectId: stateParam,
        });
        jiraChannel.postMessage({
          type: 'JIRA_CONFIG_CHANGED',
          projectId: stateParam,
          timestamp: Date.now(),
        });
        setStep(2);
      } catch (error) {
        setError('Error saving site selection. Please try again.');
      }
    }
  };

  useEffect(() => {
    callJira();
  }, []);

  const handleRedirect = () => {
    if (stateParam) {
      navigate(`/project/${stateParam}/configurations?tab=jira`);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Container className="mt-4">
      {loading ? (
        <Typography variant="body1" className="text-muted" align="center">
          Authenticating Jira and fetching data...
        </Typography>
      ) : error ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <Typography variant="h6" color="error" align="center" sx={{ mb: 2 }}>
            Oops! Something went wrong.
          </Typography>
          <Typography
            variant="body1"
            color="error"
            align="center"
            sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleRedirect}
            sx={{
              textTransform: 'none',
              textAlign: 'center',
            }}>
            Go back
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            padding: '40px',
            maxWidth: '600px',
            margin: 'auto',
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}>
          {step === 1 ? (
            <>
              <Typography variant="h5" align="center" gutterBottom>
                Complete Integration
              </Typography>
              <Typography variant="body2" align="center" sx={{ mb: 4 }}>
                To complete the Jira integration, please select a Jira site
                below. This site will be used to fetch projects, issues, and
                other relevant data. If you do not select a site, the
                integration will not be completed.
              </Typography>

              <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                <InputLabel id="jira-site-select-label">Jira Site</InputLabel>
                <Select
                  labelId="jira-site-select-label"
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  label="Jira Site">
                  {jiraSites.map((site) => (
                    <MenuItem
                      key={site.id}
                      value={site.id}
                      sx={{
                        textTransform: 'none !important',
                      }}>
                      {site.url}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="body2"
                align="center"
                color={'error'}
                sx={{ mb: 3 }}>
                Ensure you have administrator access to the selected Jira site
                to fetch data effectively.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedSite}
                onClick={handleSubmit}
                fullWidth>
                Continue
              </Button>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <IconButton
                  onClick={() => setStep(1)}
                  aria-label="back"
                  sx={{ mb: 1 }}>
                  <ArrowBack />
                </IconButton>
              </Grid>
              <Typography variant="h5" align="center" gutterBottom>
                Select Projects
              </Typography>
              <Typography variant="body2" align="center" sx={{ mb: 4 }}>
                Please select the JIRA projects you want to access from GAINSS
                to complete the integration.
              </Typography>
              <JiraProjectSelection
                projectId={stateParam}
                handleClose={handleRedirect}
              />
            </>
          )}
        </Box>
      )}
    </Container>
  );
};

export default JiraCallbackComponent;
